import { SubscriptionProductCodes } from "@rightship-tech/common-ui/models/rightstore/rightstore.models";
import { autoinject, BindingEngine, computedFrom, observable, transient } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { Claims } from "../../../../constants/authentication/claims";
import { MobileHelper } from "../../../../helpers/mobile-helper";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../models/basics/basics.models";
import { DataGridColumn, DataType, QueryFilter, SourceType } from "../../../../models/queries/queries.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { ManageRightShipUsersClientService } from "../../../../services/modules/righship-qi-manage-users.service";
import { Guid } from "../../../../utilities/guid";
import { EntityFinderSearchFilter } from "../../../components/entity-finder-search-filter/entity-finder-search-filter";
import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import KeyDownEvent = JQuery.KeyDownEvent;

@autoinject()
@transient()
export class Companies extends CustomGridPageBase {
    isMobileFilterOpen: boolean;
    @observable()
    filter: string;
    appliedFilter: string;
    public defaultPageCount: number = 100;
    public rightShipUser: boolean;
    public initialFilters: QueryFilter[];
    public countryEntityTypes: EntityTypeConfigurationModel[];
    public isLimitedSubscription: boolean;

    //country filter
    public countryEntityFinderFilter: EntityFinderSearchFilter;
    public showCountryFilter: boolean = false;
    public selectedCountries: number[] = [];
    public clearCountryOnShow: boolean;

    //registration country filter
    public registrationCountryEntityFinderFilter: EntityFinderSearchFilter;
    public showRegistrationCountryFilter: boolean = false;
    public selectedRegistrationCountries: number[] = [];
    public clearRegistrationCountryOnShow: boolean;

    //control country filter
    public controlCountryEntityFinderFilter: EntityFinderSearchFilter;
    public showControlCountryFilter: boolean = false;
    public selectedControlCountries: number[] = [];
    public clearControlCountryOnShow: boolean;

    //vessel Under Management filter 
    public showVesselUnderManagementFilter: boolean = false;

    public vUMdefaultValues : {low : number, high : number} = {low : 0, high: 1000000};
    public selectedVesselUnderManagementRange_First: number = this.vUMdefaultValues.low;
    public selectedVesselUnderManagementRange_Second: number = this.vUMdefaultValues.high;
    public clearVesselUnderManagementOnShow: boolean;

    //Role filter
    public showRoleFilter: boolean = false;
    public selectedRoles: number[] = []
    public clearRolesOnShow: boolean;
    public roleList =
        [
            { id: 1, name: 'Beneficial Owner (BO)', value: 'BO', isSelected: false, propertyName: 'hasBo' },
            { id: 2, name: 'Commercial Manager (CM)', value: 'CM', isSelected: false, propertyName: 'hasCm' },
            { id: 5, name: 'Commercial Operator (CO)', value: 'CO', isSelected: false, propertyName: 'hasCo' },
            { id: 6, name: 'Document of Compliance (DOC)', value: 'DOC', isSelected: false, propertyName: 'hasDoc' },
            { id: 4, name: 'Registered Owner (RO)', value: 'RO', isSelected: false, propertyName: 'hasRo' },
            { id: 3, name: 'Technical Manager (TM)', value: 'TM', isSelected: false, propertyName: 'hasTm' }
        ]

    //Mobile Sorting
    public showColumnSorting: boolean = false;
    public selectedColumnSorting: number[] = [];

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private userClaimsService: UserClaimsService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper, private manageRightShipUsersClientService: ManageRightShipUsersClientService) {
        super(i18N, "Company", bindingEngine);
        
        this.countryEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Countries"]);
        this.rightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.columns.push();
        this.initialSorting = this.getSorting();
    }

    async activate() {
        this.isLimitedSubscription = await this.manageRightShipUsersClientService.isLimitedCustomerSubscription(SubscriptionProductCodes.informationServices);

        this.roleList.filter(x => x.value == 'TM' || x.value == 'DOC').forEach(x => {
            this.selectedRoles.push(x.id);
            x.isSelected = true;
        })
        this.columns = this.getColumns();
        this.setItemSource();
    }

    public getSorting() {
        const columns: DataGridColumn[] = [];
        columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: 1,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'docCount',
                title: 'Vessels under management'
            });
        return columns;
    }

    private getItemSource(filters) {
        this.initialFilters = filters;
        return {
            sourceType: SourceType.ODataUrl,
            source: "odata/Companies",
            columns: this.columns,
            initialFilters: filters,
            initialSorting: this.initialSorting
        };
    }

    private setItemSource() {

        const currentFilters: QueryFilter[] = [];

        if (this.countryFilterActive) {

            const countryFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "countryId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedCountries || [],
                isAnd: true,
                skipOnNullOrEmpty: true
            };

            currentFilters.push(countryFilter);
        }

        if (this.registrationCountryFilterActive) {
            const registrationCountryFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "registrationCountryId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedRegistrationCountries || [],
                isAnd: true,
                skipOnNullOrEmpty: true
            };

            currentFilters.push(registrationCountryFilter);
        }

        if (this.controlCountryFilterActive) {
            const controlCountryFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "controlCountryId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedControlCountries || [],
                isAnd: true,
                skipOnNullOrEmpty: true
            };

            currentFilters.push(controlCountryFilter);
        }

        if (this.roleFilterActive) {
            const selectedRoleList = this.roleList.filter(x => this.selectedRoles.some(sr => sr == x.id))
            for (let i = 0; i < selectedRoleList.length; i++) {
                const roleFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} eq ({1})",
                        friendlyName: "eq",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: selectedRoleList[i].propertyName,
                        isDynamicProperty: false,
                        type: DataType.Boolean,
                        isNullable: true,
                    },
                    filterValue: selectedRoleList[i].isSelected || [],
                    isAnd: true,
                    skipOnNullOrEmpty: true
                };
                currentFilters.push(roleFilter);
            }
        }

        const vesselUnderManagementForGreaterThenFilter: QueryFilter = {
            filterOperator: {
                translatedSymbol: "{0} ge {1}",
                friendlyName: "ge",
                requiresFilterValue: true,
                requiresInterpolation: true
            },
            filterProperty: {
                name: "docCount",
                isDynamicProperty: false,
                type: DataType.Number,
                isNullable: true
            },
            filterValue: this.selectedVesselUnderManagementRange_First,
            isAnd: true,
            skipOnNullOrEmpty: true
        }
        currentFilters.push(vesselUnderManagementForGreaterThenFilter);

        const vesselUnderManagementForLowerThenFilter: QueryFilter = {
            filterOperator: {
                translatedSymbol: "{0} le {1}",
                friendlyName: "le",
                requiresFilterValue: true,
                requiresInterpolation: true
            },
            filterProperty: {
                name: "docCount",
                isDynamicProperty: false,
                type: DataType.Number,
                isNullable: true
            },
            filterValue: this.selectedVesselUnderManagementRange_Second,
            isAnd: true,
            skipOnNullOrEmpty: true
        }
        currentFilters.push(vesselUnderManagementForLowerThenFilter);

        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("fullName", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("owCode", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }

        this.itemSource = this.getItemSource(currentFilters);
    }

    clearFilter() {
        this.filter = "";
        this.appliedFilter = this.filter;

        this.setItemSource();
    }

    clearAllFilters($event, reload: boolean = true) {
        if ($event)
            $event.stopPropagation();
        if(this.dataGridVM)
        {
            this.dataGridVM.clearSorting();
        }
        this.appliedFilter = null;
        this.filter = null;
        this.selectedCountries = [];
        this.selectedRegistrationCountries = [];
        this.selectedControlCountries = [];
        this.selectedRoles = [];

        if (this.countryEntityFinderFilter) {
            this.countryEntityFinderFilter.clear();
        }
        else {
            this.clearCountryOnShow = true;
        }

        if (this.registrationCountryEntityFinderFilter) {
            this.registrationCountryEntityFinderFilter.clear();
        }
        else {
            this.clearRegistrationCountryOnShow = true;
        }

        if (this.controlCountryEntityFinderFilter) {
            this.controlCountryEntityFinderFilter.clear();
        }
        else {
            this.clearControlCountryOnShow = true;
        }

        this.clearVesselUnderManagementFilter();
         
        this.closeAllFilters();

        if (reload)
            this.setItemSource();
    }

    public closeAllFilters() {
        this.showCountryFilter = false;
        this.showRegistrationCountryFilter = false;
        this.showControlCountryFilter = false;
        this.showRoleFilter = false;
        this.showVesselUnderManagementFilter = false;
        return false;
    }

    public selectedFilter: { title: string, clear: () => void, apply: () => void };

    public getCompanyItemRoute = (id) => `/company/${id}`;

    getColumns(): DataGridColumn[] {
        return [
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                name: 'fullName',
                title: 'Name',
                value: '<a class="link" href.bind="getCompanyItemRoute(item.id)">${item.fullName}</a>',
                isSortable: true,
                instancePerRow: true,
                parameter: {
                    getCompanyItemRoute: this.getCompanyItemRoute.bind(this),
                    item: null
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? '' : '380px;'
            },
            {
                dataType: DataType.CustomTemplate,
                value: '<comp-safety-score score.bind="item.docSubscore" safety-score-type="-3"' +
                        'is-condensed="true" is-doc-subscore="true" has-doc-hybrid-rule.bind="item.hasHybridRule" ' +
                        'mask-data.bind="maskData(item.docSubscore)" locked-feature.bind="lockedFeature"></comp-safety-score>',
                name: "docSubscore",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "DOC Subscore",
                instancePerRow: true,
                sortOrder: 2,
                columnOrder: 2,
                isSortable: true,
                parameter: {
                    item: null,
                    lockedFeature: this.isLimitedSubscription,
                    maskData: (docSubscore) => {
                        return docSubscore === undefined;
                    }
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "220px"
            },
            {
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Default,
                name: 'country',
                title: 'Country',
                isSortable: true,
                tooltipForTitle: 'Country of domicile, physical location of the company.'
            },
            {
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                name: 'registrationCountry',
                title: 'Nationality of Registration',
                isSortable: true,
                tooltipForTitle: 'Country where the company is legally registered as an entity.'
            },
            {
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.Default,
                name: 'controlCountry',
                title: 'Nationality of Control',
                isSortable: true,
                tooltipForTitle: 'Actual nationality of company regardless of physical location.'
            },
            {
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.Number,
                name: 'docCount',
                title: 'Vessels under management',
                isSortable: true,
                tooltipForTitle: 'Vessels that this company holds a DOC role.'
            }];
    }

    public toggleCountryFilters() {
        if (!this.showCountryFilter) {
            this.closeAllFilters();
        }
        this.showCountryFilter = !this.showCountryFilter;
        this.selectedFilter = {
            title: "Country",
            clear: () => {
                this.clearCountryFilter();
            },
            apply: () => {
                this.applyCountryFilter(this.selectedCountries.map(x => {
                    return { id: x }
                }))
            }
        }
    }

    public toggleRegistrationCountryFilters() {
        if (!this.showRegistrationCountryFilter) {
            this.closeAllFilters();
        }
        this.showRegistrationCountryFilter = !this.showRegistrationCountryFilter;
        this.selectedFilter = {
            title: "RegistrationCountry",
            clear: () => {
                this.clearRegistrationCountryFilter();
            },
            apply: () => {
                this.applyRegistrationCountryFilter(this.selectedRegistrationCountries.map(x => {
                    return { id: x }
                }))
            }
        }
    }

    public toggleControlCountryFilters() {
        if (!this.showControlCountryFilter) {
            this.closeAllFilters();
        }
        this.showControlCountryFilter = !this.showControlCountryFilter;
        this.selectedFilter = {
            title: "ControlCountry",
            clear: () => {
                this.clearControlCountryFilter();
            },
            apply: () => {
                this.applyControlCountryFilter(this.selectedControlCountries.map(x => {
                    return { id: x }
                }))
            }
        }
    }

    public toggleVesselUnderManagementFilters() {
        if (!this.showVesselUnderManagementFilter) {
            this.closeAllFilters();
        }
        this.showVesselUnderManagementFilter = !this.showVesselUnderManagementFilter;
        this.selectedFilter = {
            title: "VesselUnderManagement",
            clear: () => {
                this.clearVesselUnderManagementFilter();
            },
            apply: () => {
                this.applyVesselUnderManagementFilter(this.selectedControlCountries.map(x => {
                    return { id: x }
                }))
            }
        }
    }

    public toggleRoleFilters() {
        if (!this.showRoleFilter) {
            this.closeAllFilters();
        }
        this.showRoleFilter = !this.showRoleFilter;
        this.selectedFilter = {
            title: "Roles",
            clear: () => {
                this.clearRoleFilter();
            },
            apply: () => {
                this.applyRoleFilter(this.selectedRoles.map(x => {
                    return { id: x }
                }))
            }
        }
    }

    public toggleColumnSortingFilters() {
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        this.selectedFilter = {
            title: "Column sorting",
            clear: () => {
            },
            apply: () => {
                this.applyColumnSortingFilter(this.selectedColumnSorting.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    sortClass(column: DataGridColumn) {
        if (column.isAscending) {
            return "Ascending"
        }
        else if (column.isAscending == false) {
            return "Descending"
        }
        else {
            return "";
        }
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }
        this.showControlCountryFilter = false;
        this.showCountryFilter = false;
        this.showRegistrationCountryFilter = false;
        this.showRoleFilter = false;
        this.showVesselUnderManagementFilter = false;
        this.showColumnSorting = false;
    }

    hideMobileFilters() {
        this.isMobileFilterOpen = false;
    }

    public applyCountryFilter(items: any[], reload: boolean = true) {
        this.selectedCountries = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleCountryFilters();
    }

    public applyRegistrationCountryFilter(items: any[], reload: boolean = true) {
        this.selectedRegistrationCountries = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleRegistrationCountryFilters();
    }

    public applyControlCountryFilter(items: any[], reload: boolean = true) {
        this.selectedControlCountries = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleControlCountryFilters();
    }

    public applyVesselUnderManagementFilter(items: any[], reload: boolean = true) {
        if (reload == true) this.setItemSource();
        this.toggleVesselUnderManagementFilters();
    }

    public applyRoleFilter(items: any[], reload: boolean = true) {
        if (reload == true) this.setItemSource();
        this.toggleRoleFilters();
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleColumnSortingFilters();
    }

    public clearCountryFilter() {
        this.selectedCountries = [];
        this.setItemSource();
    }

    public clearRegistrationCountryFilter() {
        this.selectedRegistrationCountries = [];
        this.setItemSource();
    }

    public clearControlCountryFilter() {
        this.selectedControlCountries = [];
        this.setItemSource();
    }

    public clearVesselUnderManagementFilter() {
        this.selectedVesselUnderManagementRange_First = 0;
        this.selectedVesselUnderManagementRange_Second = 1000000;
        this.setItemSource();
    }

    public clearRoleFilter() {
        this.selectedRoles = [];
        this.roleList.forEach(x => {
            x.isSelected = false;
        });
        this.setItemSource();
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();

        this.appliedFilter = this.filter;
        this.setItemSource();
    }

    public get isSubFilterOpen(): boolean {
        return this.showColumnSorting || this.showControlCountryFilter || this.showCountryFilter || this.showRegistrationCountryFilter || this.showRoleFilter
            || this.showVesselUnderManagementFilter;
    }

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }

    sortByMobile(column: DataGridColumn) {
        this.dataGridVM.sortingTriggered(column);
    }

    public get mobileFilterSelected() {
        return this.showColumnSorting || this.showControlCountryFilter || this.showCountryFilter || this.showRegistrationCountryFilter || this.showRoleFilter
            || this.showVesselUnderManagementFilter;
    }

    @computedFrom("selectedCountries", "selectedCountries.length")
    public get countryFilterActive() {
        return this.selectedCountries?.length > 0;
    }

    @computedFrom("selectedRegistrationCountries", "selectedRegistrationCountries.length")
    public get registrationCountryFilterActive() {
        return this.selectedRegistrationCountries?.length > 0;
    }

    @computedFrom("selectedControlCountries", "selectedControlCountries.length")
    public get controlCountryFilterActive() {
        return this.selectedControlCountries?.length > 0;
    }

    @computedFrom("selectedVesselUnderManagementRange_First", "selectedVesselUnderManagementRange_Second")
    public get vesselsUnderManagementFilterActive() {
        return this.selectedVesselUnderManagementRange_First > this.vUMdefaultValues.low ||
            this.selectedVesselUnderManagementRange_Second < this.vUMdefaultValues.high;
    }

    @computedFrom("selectedRoles", "selectedRoles.length")
    public get roleFilterActive() {
        return this.selectedRoles?.length > 0;
    }

    @computedFrom("initialFilters")
    public get filtersActive() {
        if (this.initialFilters.length == 2) {
            // If the current active filters are the default range selections
            if (this.initialFilters[0].filterValue === this.vUMdefaultValues.low &&
                this.initialFilters[1].filterValue === this.vUMdefaultValues.high) {
                return false;
            }
        }
        return true;
    }

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }
}